import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { config } from '../../../config';
import { LocalStorageKeys, getTransformedDataForPagination, objectToQueryParams } from '../../../utils';


interface WorkLogBase {
    notes?: string; 
    log_date: string;
    hours_spent: number;
    task_id: string;
}

export interface IWorkLog extends WorkLogBase {
    created_time?: string; // ISO 8601 date-time format
    created_user?: string;
    created_user_id?: string;
    modified_time?: string; // ISO 8601 date-time format
    modified_user?: string;
    modified_user_id?: string;
    sequence_id?: string; // ReadOnly
    version?: number; // ReadOnly
    _id: string; // Must follow pattern: ^[a-f\d]{24}$
}


interface WorkLogGetMethodParams {
    search?: string, 
    task_id?: string,
    user_id?: string
}

interface SuccessResponse {
    sequence_id?: string; // ReadOnly
    version?: number; // ReadOnly
    _id: string; // Required, Must follow pattern: ^[a-f\d]{24}$
    message: string;
    url?: string; // Optional URL for document upload/download
}

export const workLogAPI = createApi({
    reducerPath: "workLogAPI",
    baseQuery: retry(fetchBaseQuery({
        baseUrl: config.api_url + "worklog",
        prepareHeaders: (headers, { getState }) => {
            const token = localStorage.getItem(LocalStorageKeys.authToken);

            // If we have a token set in state, let's assume that we should be passing it.
            if (token) {
                headers.set('authorization', `Bearer ${token}`)
            }

            return headers
        },
    }), { maxRetries: 1 }),
    endpoints: (builder) => ({
        // Get Work Logs API
        getWorkLogs: builder.query<{ data: IWorkLog[], totalCount: number }, WorkLogGetMethodParams>({
            query: (payload) => ({ url: objectToQueryParams(payload), method: "GET" }),
            transformResponse: (data: IWorkLog[], meta: { request: Request; response?: Response }) => {
                return getTransformedDataForPagination(data, meta?.response);
            },
        }),

        // Get Work Log by ID API
        getWorkLogById: builder.query<IWorkLog, string>({
            query: (id) => ({ url: `/${id}`, method: "GET" }),
        }),

        // Create Work Log API
        createWorkLog: builder.mutation<SuccessResponse, WorkLogBase>({
            query: (payload) => ({ method: "POST", body: payload }),
        }),

        // Update Work Log API
        updateWorkLog: builder.mutation<SuccessResponse, { id: string, payload: WorkLogBase, update_version?: boolean }>({
            query: (payload) => ({ url: `/${payload.id}?update_version=${payload.update_version ? true : false}`, method: "PUT", body: payload.payload }),
        }),

        // Delete Work Log API
        deleteWorkLog: builder.mutation<string, string>({
            query: (id) => ({ url: `/${id}`, method: "DELETE" }),
        }),
    })
})

// Export hooks for usage in functional components
export const {
    useGetWorkLogByIdQuery, useLazyGetWorkLogsQuery, useGetWorkLogsQuery, useLazyGetWorkLogByIdQuery,
    useCreateWorkLogMutation, useUpdateWorkLogMutation, useDeleteWorkLogMutation
} = workLogAPI